import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, HelpCircle, MessageCircle, Mail } from "lucide-react";
import useSeo from "../hooks/useSeo";
import useCmsContent from "../hooks/useCmsContent";
import { useCompanyContact, waLink } from "../lib/companyContact";

const FAQ_DEFAULTS = {
  title: "Preguntas Frecuentes",
  subtitle: "Encuentra respuesta a las dudas más comunes sobre nuestra plataforma de renta de autos entre particulares.",
};

const FAQS = [
  {
    q: "¿Qué es RivoNes?",
    a: (
      <p>
        RivoNes es una plataforma de renta de autos entre particulares en México.
        Conectamos propietarios de vehículos ("Hosts") con usuarios que buscan rentar un auto
        de forma rápida, segura y completamente digital.
      </p>
    ),
  },
  {
    q: "¿Cómo funciona RivoNes?",
    a: (
      <ol className="list-decimal pl-5 space-y-1">
        <li>El usuario crea una cuenta.</li>
        <li>Busca vehículos disponibles en su ciudad.</li>
        <li>Selecciona fechas y realiza la reserva.</li>
        <li>El Host aprueba la solicitud.</li>
        <li>Ambas partes coordinan la entrega del vehículo.</li>
        <li>El usuario disfruta el viaje y al finalizar ambos pueden calificarse mutuamente.</li>
      </ol>
    ),
  },
  {
    q: "¿Quién puede rentar un vehículo?",
    a: (
      <>
        <p className="mb-2">Para rentar un vehículo en RivoNes debes:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Tener identificación oficial vigente.</li>
          <li>Licencia de conducir válida.</li>
          <li>Ser mayor de edad conforme a las leyes aplicables en México.</li>
          <li>Completar el proceso de verificación de identidad.</li>
        </ul>
      </>
    ),
  },
  {
    q: "¿Cómo puedo publicar mi vehículo?",
    a: (
      <>
        <p className="mb-2">Solo necesitas:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Crear una cuenta.</li>
          <li>Subir fotos de tu vehículo.</li>
          <li>Agregar información del automóvil.</li>
          <li>Subir documentación requerida.</li>
          <li>Configurar precios y disponibilidad.</li>
        </ul>
        <p className="mt-2">
          Una vez aprobado, tu vehículo aparecerá disponible para renta en la plataforma.
          Revisa todos los{" "}
          <Link to="/requisitos-host" className="text-terracotta font-semibold hover:underline">
            requisitos para anfitriones
          </Link>
          .
        </p>
      </>
    ),
  },
  {
    q: "¿Qué documentos necesito para registrarme como Host?",
    a: (
      <>
        <p className="mb-2">Dependiendo del vehículo y ubicación, podemos solicitar:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>INE o pasaporte.</li>
          <li>Licencia de conducir.</li>
          <li>Tarjeta de circulación.</li>
          <li>Comprobante de propiedad o factura.</li>
          <li>Póliza de seguro vigente.</li>
          <li>Selfie o verificación biométrica facial.</li>
        </ul>
      </>
    ),
  },
  {
    q: "¿Cómo se verifica la identidad?",
    a: (
      <>
        <p className="mb-2">RivoNes utiliza procesos de validación digital que pueden incluir:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Verificación facial biométrica.</li>
          <li>Comparación de selfie con identificación oficial.</li>
          <li>Validación de documentos.</li>
          <li>Confirmación de correo electrónico y teléfono.</li>
        </ul>
        <p className="mt-2">
          Esto ayuda a mantener una comunidad más segura y confiable.
        </p>
      </>
    ),
  },
  {
    q: "¿Los vehículos cuentan con seguro?",
    a: (
      <>
        <p>
          Los vehículos deben contar con seguro vigente conforme a las leyes mexicanas.
          Dependiendo de la reserva, pueden aplicarse coberturas adicionales, protección
          opcional y depósitos de seguridad.
        </p>
        <p className="mt-2">
          Recomendamos revisar los detalles específicos de cobertura antes de confirmar
          cualquier reserva.
        </p>
      </>
    ),
  },
  {
    q: "¿Qué sucede si el vehículo sufre daños?",
    a: (
      <>
        <p className="mb-2">
          Cualquier incidente debe reportarse inmediatamente a través de la plataforma.
          Dependiendo del caso, podrán aplicarse:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Depósitos de seguridad.</li>
          <li>Coberturas de protección.</li>
          <li>Cobros por daños.</li>
          <li>Procesos de reclamación.</li>
        </ul>
        <p className="mt-2">
          Toda evidencia debe documentarse con fotografías y reportes correspondientes.
        </p>
      </>
    ),
  },
  {
    q: "¿Cómo se realizan los pagos?",
    a: (
      <p>
        Los pagos se realizan de manera digital mediante métodos autorizados dentro de la
        plataforma. Esto ayuda a proteger tanto al usuario como al Host.
      </p>
    ),
  },
  {
    q: "¿Cuándo recibe el pago el Host?",
    a: (
      <p>
        Los pagos normalmente se procesan después del inicio de la renta y pueden variar
        dependiendo del método de pago, verificaciones de seguridad y tiempos bancarios.
      </p>
    ),
  },
  {
    q: "¿Puedo cancelar una reserva?",
    a: (
      <p>
        Sí. Las cancelaciones están sujetas a la{" "}
        <Link to="/cancelacion" className="text-terracotta font-semibold hover:underline">
          política de cancelación de RivoNes
        </Link>
        . Dependiendo del momento de cancelación, podrían aplicarse cargos o reembolsos
        parciales.
      </p>
    ),
  },
  {
    q: "¿Qué pasa si el Host cancela?",
    a: (
      <p>
        Si un Host cancela una reserva confirmada, RivoNes puede ayudar al usuario a
        encontrar otro vehículo disponible o procesar el reembolso correspondiente.
      </p>
    ),
  },
  {
    q: "¿Cómo se entregan los vehículos?",
    a: (
      <>
        <p className="mb-2">La entrega puede realizarse:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>En persona.</li>
          <li>En ubicaciones acordadas.</li>
          <li>En aeropuertos.</li>
          <li>Mediante opciones de entrega configuradas por el Host.</li>
        </ul>
        <p className="mt-2">Los detalles se muestran antes de confirmar la reserva.</p>
      </>
    ),
  },
  {
    q: "¿Puedo agregar conductores adicionales?",
    a: (
      <p>
        Sí, siempre que cumplan con los requisitos de verificación y sean aprobados dentro
        de la plataforma.
      </p>
    ),
  },
  {
    q: "¿Cómo funcionan las calificaciones?",
    a: (
      <>
        <p className="mb-2">Después de cada renta:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>El usuario puede calificar al Host y al vehículo.</li>
          <li>El Host puede calificar al usuario.</li>
        </ul>
        <p className="mt-2">
          Esto ayuda a mantener altos estándares de seguridad, limpieza y profesionalismo.
        </p>
      </>
    ),
  },
  {
    q: "¿Qué vehículos están permitidos?",
    a: (
      <p>
        Los vehículos deben cumplir ciertos requisitos de seguridad, documentación y
        condiciones mecánicas. RivoNes puede rechazar vehículos que no cumplan los estándares
        mínimos.
      </p>
    ),
  },
  {
    q: "¿Qué hago en caso de accidente?",
    a: (
      <ol className="list-decimal pl-5 space-y-1">
        <li>Prioriza la seguridad.</li>
        <li>Contacta a emergencias si es necesario.</li>
        <li>Reporta el incidente inmediatamente.</li>
        <li>Toma fotografías y evidencia.</li>
        <li>Sigue las instrucciones proporcionadas por el seguro y la plataforma.</li>
      </ol>
    ),
  },
  {
    q: "¿RivoNes está disponible en todo México?",
    a: (
      <p>
        Actualmente estamos expandiendo nuestros servicios en diferentes ciudades de México.
        Algunas funciones y disponibilidad pueden variar según la ubicación.
      </p>
    ),
  },
  {
    q: "¿Cómo puedo contactar soporte?",
    a: (
      <>
        <p className="mb-2">Puedes comunicarte con el equipo de soporte mediante:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Correo electrónico</li>
          <li>WhatsApp</li>
          <li>Formulario de contacto en la plataforma</li>
        </ul>
        <p className="mt-2">
          También puedes visitar el{" "}
          <a
            href="https://rivones.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-terracotta font-semibold hover:underline"
          >
            sitio oficial de RivoNes
          </a>
          .
        </p>
      </>
    ),
  },
];

function FaqItem({ q, a, idx, open, onToggle }) {
  return (
    <li
      className="border-b border-neutral-200 last:border-0"
      data-testid={`faq-item-${idx}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 py-4 text-left hover:text-terracotta transition-colors"
        aria-expanded={open}
        data-testid={`faq-toggle-${idx}`}
      >
        <span className="font-semibold text-[#111111]">{q}</span>
        <ChevronDown
          className={`w-5 h-5 flex-shrink-0 text-neutral-400 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open && (
        <div
          className="pb-4 text-neutral-700 leading-relaxed"
          data-testid={`faq-answer-${idx}`}
        >
          {a}
        </div>
      )}
    </li>
  );
}

export default function PreguntasFrecuentesPage() {
  const [open, setOpen] = useState(0);
  const { content: cms } = useCmsContent("faq", FAQ_DEFAULTS);
  const { contact } = useCompanyContact();

  useSeo({
    title: "Preguntas Frecuentes · Rivones",
    description:
      "Respuestas a las preguntas más comunes sobre Rivones: cómo funciona, requisitos, pagos, seguros, cancelaciones y soporte.",
    keywords: "faq rivones, preguntas frecuentes, ayuda rivones, soporte rivones",
    canonical: "https://rivones.com/preguntas-frecuentes",
  });

  return (
    <div className="bg-sand min-h-screen" data-testid="faq-page">
      {/* Hero */}
      <section className="bg-gradient-to-b from-neutral-50 to-sand border-b border-neutral-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <p className="text-sm font-semibold tracking-[0.2em] text-terracotta uppercase">
            Ayuda
          </p>
          <h1
            className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-[#111111] tracking-tight mt-3"
            data-testid="faq-h1"
          >
            {cms.title}
          </h1>
          <p className="text-base sm:text-lg text-neutral-600 mt-4 leading-relaxed">
            {cms.subtitle}
          </p>
        </div>
      </section>

      {/* FAQ list */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="bg-white border border-neutral-200 rounded-2xl p-2 sm:p-4">
          <ul>
            {FAQS.map((f, i) => (
              <FaqItem
                key={i}
                q={f.q}
                a={f.a}
                idx={i}
                open={open === i}
                onToggle={() => setOpen(open === i ? -1 : i)}
              />
            ))}
          </ul>
        </div>

        {/* Contact CTA */}
        <div className="mt-10 p-6 rounded-2xl bg-gradient-to-br from-terracotta/10 to-amber-50 border border-terracotta/20 text-center">
          <HelpCircle className="w-10 h-10 text-terracotta mx-auto mb-3" />
          <h3 className="font-heading font-semibold text-xl text-agave mb-1">
            ¿No encontraste lo que buscabas?
          </h3>
          <p className="text-sm text-neutral-700 mb-4">
            Nuestro equipo está listo para ayudarte. Escríbenos por email o WhatsApp.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <a
              href={`mailto:${contact.support_email}`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#111111] text-white text-sm font-medium hover:bg-neutral-800 transition-colors"
              data-testid="faq-contact-email"
            >
              <Mail className="w-4 h-4" />
              {contact.support_email}
            </a>
            <a
              href={waLink(contact.whatsapp_number)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition-colors"
              data-testid="faq-contact-whatsapp"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
